export type LayoutRuntime = {
  init: () => void;
  cleanup: () => void;
};

function setCssVar(name: string, value: string) {
  document.documentElement.style.setProperty(name, value);
}

export function createLayoutRuntime(): LayoutRuntime {
  let cleanupFn: (() => void) | null = null;

  function init() {
    cleanupFn?.();

    const tg = (window as any).Telegram?.WebApp as any;

    const updateVars = () => {
      const vh = tg?.viewportHeight || window.innerHeight;
      const vsh = tg?.viewportStableHeight || vh;
      setCssVar('--tg-viewport-height', `${vh}px`);
      setCssVar('--tg-viewport-stable-height', `${vsh}px`);

      const deviceTop = tg?.safeAreaInset?.top ?? 0;
      const tgUiTop = tg?.contentSafeAreaInset?.top ?? 0;
      const deviceBottom = tg?.safeAreaInset?.bottom ?? 0;
      const tgUiBottom = tg?.contentSafeAreaInset?.bottom ?? 0;

      const safeTop = deviceTop + tgUiTop;
      const safeBottom = deviceBottom + tgUiBottom;
      setCssVar('--tg-safe-top', `${safeTop}px`);
      setCssVar('--tg-safe-bottom', `${safeBottom}px`);

      // Force reflow for fixed elements (helps with orientation change glitches)
      void document.documentElement.offsetHeight;

      window.dispatchEvent(new CustomEvent('aicams-layout-updated'));
    };

    // Non-Telegram fallback
    if (!tg) {
      updateVars();
      const onResize = () => updateVars();
      window.addEventListener('resize', onResize);
      cleanupFn = () => window.removeEventListener('resize', onResize);
      return;
    }

    try { tg.ready?.(); } catch {}
    // expand() removed - allow mini app to start collapsed

    updateVars();
    const onResize = () => updateVars();
    const onVisibility = () => {
      if (document.visibilityState === 'visible') updateVars();
    };

    // Handle orientation change with delayed recalculation
    // Telegram WebView sometimes reports incorrect viewport after rotation
    const onOrientationChange = () => {
      // Immediate update
      updateVars();
      // Delayed updates to catch late viewport corrections
      setTimeout(() => {
        updateVars();
      }, 100);
      setTimeout(() => updateVars(), 300);
      setTimeout(() => updateVars(), 500);
    };

    try { tg.onEvent?.('viewportChanged', updateVars); } catch {}
    window.addEventListener('resize', onResize);
    window.addEventListener('orientationchange', onOrientationChange);
    document.addEventListener('visibilitychange', onVisibility);

    cleanupFn = () => {
      try { tg.offEvent?.('viewportChanged', updateVars); } catch {}
      window.removeEventListener('resize', onResize);
      window.removeEventListener('orientationchange', onOrientationChange);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }

  return {
    init,
    cleanup: () => cleanupFn?.(),
  };
}


